import { Spin } from "antd";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { pifGet } from "../../../helpers/api_helper";
import { GET_PIF_DashboardSummary } from "../../../helpers/url_helper";
import { PIF_INTEGRATION_STATUS, pifDateTime } from "./pifConstants";

const fmtDateTime = pifDateTime;

const IntegrationDashboard = () => {
  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));
  const companyIdEnc = AuthUser?.CompanyIDEncrypt;

  const [isLoading, setIsLoading] = useState(false);
  const [counts, setCounts] = useState(null);
  const [health, setHealth] = useState(null);
  const [loadError, setLoadError] = useState("");

  const getData = async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const res = await pifGet(
        `${GET_PIF_DashboardSummary}?companyIdEnc=${encodeURIComponent(companyIdEnc)}`
      );
      setCounts(res?.Counts || null);
      setHealth(res?.Health || null);
    } catch (e) {
      setLoadError(
        "Could not reach the Tally Integration service. It may be offline."
      );
      setCounts(null);
      setHealth(null);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hbAge = health?.LastHeartbeatAgeMinutes ?? null; // server-computed, not the browser clock
  const agentDown = hbAge == null || hbAge > 10;
  const statusInfo =
    PIF_INTEGRATION_STATUS[health?.Status] || { label: "Not configured", badge: "secondary" };

  const cards = [
    { label: "Pending", value: counts?.PendingCount, icon: "fa-hourglass-half", tone: "primary", qs: "status=1" },
    { label: "Failed / Dead", value: counts?.FailedCount, icon: "fa-triangle-exclamation", tone: "warning", qs: "status=5" },
    { label: "Blocked", value: counts?.BlockedCount, icon: "fa-ban", tone: "danger", qs: "status=4" },
    { label: "Synced (30 days)", value: counts?.SyncedThisMonth, icon: "fa-circle-check", tone: "success", qs: "status=3" },
  ];

  return (
    <div className="m-1">
      <Spin size="large" spinning={isLoading} tip={"Loading..."} style={{ position: "fixed" }}>
        <div className="card mb-2">
          <div className="card-body p-2 d-flex flex-wrap align-items-center justify-content-between">
            <div>
              <h4 className="heading mb-0">Tally Integration</h4>
              <small className="text-muted">
                Approved invoices sync to Tally automatically. Pragya stays the system of record.
              </small>
            </div>
            <button className="btn btn-outline-primary btn-sm" onClick={getData}>
              <i className="fa-solid fa-refresh me-2" /> Refresh
            </button>
          </div>
        </div>

        {loadError ? (
          <div className="alert alert-warning py-2">{loadError}</div>
        ) : health == null && !isLoading ? (
          <div className="alert alert-info py-2">
            Tally Integration is not configured for this company yet.
          </div>
        ) : (
          <>
            <div className="card mb-2">
              <div className="card-body p-2">
                <div className="d-flex flex-wrap align-items-center">
                  <span className={`badge badge-${agentDown ? "danger" : "success"} light me-3`}>
                    <i className={`fa-solid ${agentDown ? "fa-plug-circle-xmark" : "fa-plug-circle-check"} me-1`} />
                    Agent {agentDown ? "not responding" : "connected"}
                  </span>
                  <span className="me-3">
                    <span className="text-muted">Tally company:</span>{" "}
                    <strong>{health?.TallyCompanyName || "—"}</strong>
                  </span>
                  <span className="me-3">
                    <span className="text-muted">Integration:</span>{" "}
                    <span className={`badge badge-${statusInfo.badge} light`}>{statusInfo.label}</span>
                  </span>
                  <span className="me-3">
                    <span className="text-muted">Agent:</span> {health?.AgentVersion || "—"}
                  </span>
                  <span className="me-3">
                    <span className="text-muted">Last heartbeat:</span>{" "}
                    {fmtDateTime(health?.LastHeartbeatUtc)}
                    {hbAge != null ? ` (${hbAge} min ago)` : ""}
                  </span>
                  <span>
                    <span className="text-muted">Last validation:</span>{" "}
                    {fmtDateTime(health?.LastValidationOnUtc)}
                  </span>
                </div>
              </div>
            </div>

            <div className="row">
              {cards.map((c) => (
                <div className="col-xl-3 col-sm-6 pe-1" key={c.label}>
                  <Link to={`/sync-queue?${c.qs}`} className="text-decoration-none">
                    <div className="widget-stat card mb-2" style={{ height: "calc(100% - 8px)" }}>
                      <div className="card-body p-3">
                        <div className="media ai-icon">
                          <span className={`me-3 bgl-${c.tone} text-${c.tone}`}>
                            <i className={`fa-solid ${c.icon} fs-3`} />
                          </span>
                          <div className="media-body">
                            <p className="mb-1">{c.label}</p>
                            <h3 className="mb-0">
                              {c.value == null ? "—" : c.value}
                            </h3>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>

            {(counts?.BlockedCount > 0 || counts?.FailedCount > 0) && (
              <div className="alert alert-danger py-2 mt-1 d-flex justify-content-between align-items-center">
                <span>
                  {counts?.BlockedCount || 0} blocked and {counts?.FailedCount || 0} failed job(s)
                  need attention.
                </span>
                <Link to="/sync-queue?status=4" className="btn btn-danger btn-sm">
                  Review
                </Link>
              </div>
            )}
          </>
        )}
      </Spin>
    </div>
  );
};

export default IntegrationDashboard;
